import { AllPositions, PitcherPositions, Teams } from "types/Player";
import Rarity from "types/Rarity";
import { TrainerTypes } from "types/Trainer";
import { faPlusSquare, faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormik } from "formik";
import { FunctionComponent, useState } from "react";
import Layout from "../Layout";
import Select from "../Select";
import S from "./styled";
import _Select from "react-select";
import { jsonSkills, jsonTrainers } from "utils/json";
import Button from "../Button";

type OwnProps = {};
type Props = OwnProps;

const PlusIcon = <FontAwesomeIcon icon={faPlusSquare} />;
const StarIcon = <FontAwesomeIcon icon={faStar} />;

const rarityOptions = Object.keys(Rarity).map((rarity) => {
  return { value: rarity, label: rarity };
});

const positionOptions = AllPositions.map((position) => {
  return { value: position, label: position };
});

const typeOptions = Object.keys(TrainerTypes).map((type) => {
  return { value: type, label: type };
});

const skillOptions = Object.entries(jsonSkills).map(([id, skill]) => {
  return { value: id, label: skill.name };
});

const teamsOptions = Object.keys(Teams).map((team) => {
  return { value: team, label: team };
});

const devBonusesOptions = [
  ...new Set(
    jsonTrainers
      .map((trainer) => {
        const bonuses = Object.values(trainer.devBonus);
        return bonuses.flat();
      })
      .flat()
  ),
].map((x) => ({ value: x, label: x }));

const TrainerPage: FunctionComponent<Props> = ({}) => {
  const formik = useFormik({
    initialValues: {
      name: "",
      rarity: Rarity.UR,
      position: PitcherPositions.SP,
      type: [],
      url: "",
      comboEvents: [],
      bonusTeam: [],
      skills: {
        1: [{ name: "", level: 1 }],
        2: [{ name: "", level: 1 }],
        3: [{ name: "", level: 1 }],
        4: [{ name: "", level: 1 }],
        5: [{ name: "", level: 1 }],
      },
      devBonus: {
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
      },
      stats: {
        "1": { STR: 0, INT: 0, DEX: 0, MNT: 0 },
        "2": { STR: 0, INT: 0, DEX: 0, MNT: 0 },
        "3": { STR: 0, INT: 0, DEX: 0, MNT: 0 },
        "4": { STR: 0, INT: 0, DEX: 0, MNT: 0 },
        "5": { STR: 0, INT: 0, DEX: 0, MNT: 0 },
      },
    },
    onSubmit: (values) => {},
    validateOnChange: true,
    validate: (values) => {},
  });

  const onSkillNameChange = (value, index) => {
    for (let i = 1; i <= 5; i++) {
      if (!formik.values.skills[i][index]) {
        continue;
      }

      const name = `skills[${i}][${index}].name`;
      formik.setFieldValue(name, value.value);
    }
  };

  const onSkillLevelChange = (e, stars, index) => {
    const value = e.target.value;

    for (let i = stars; i <= 5; i++) {
      const name = `skills[${i}][${index}].level`;
      const maxValue = i === stars ? value : Math.max(value, +formik.values.skills[i][index].level || 0);

      formik.setFieldValue(name, maxValue);
    }
  };

  const addSkill = (stars) => {
    for (let i = stars; i < 6; i++) {
      const size = formik.values.skills[i].length;

      formik.setFieldValue(`skills[${i}][${size}].name`, "");
      formik.setFieldValue(`skills[${i}][${size}].level`, 1);
    }
  };

  const onDevBonusChange = (value, stars) => {
    const oldValue = formik.values.devBonus[stars];

    if (oldValue.length > value.length) {
      const deleted = oldValue.find((x) => !value.includes(x));

      for (let i = stars; i <= 5; i++) {
        const current = formik.values.devBonus[i];
        formik.setFieldValue(
          `devBonus[${i}]`,
          current.filter((x) => x !== deleted)
        );
      }
    } else {
      const added = value.find((x) => !oldValue.includes(x));

      for (let i = stars; i <= 5; i++) {
        const current = formik.values.devBonus[i];
        formik.setFieldValue(`devBonus[${i}]`, [...current, added]);
      }
    }
  };

  const onExportJson = () => {
    const data = formik.values;

    const skills = Object.entries(formik.values.skills).map(([level, skills]) => {
      const _skills = skills.reduce((accum, curr) => {
        accum[curr.name] = curr.level;

        return accum;
      }, {});

      return [level, _skills];
    });

    data.skills = Object.fromEntries(skills);

    const devBonus = Object.entries(formik.values.devBonus).map(([level, bonus]) => {
      const _bonus = bonus.map((x) => x.value);

      return [level, _bonus];
    });

    data.devBonus = Object.fromEntries(devBonus);

    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(data));
    const dlAnchorElem = document.getElementById("downloadAnchorElem");
    dlAnchorElem.setAttribute("href", dataStr);
    dlAnchorElem.setAttribute("download", "trainer.json");
    dlAnchorElem.click();
  };

  return (
    <Layout>
      <S.Container>
        <div>
          <form>
            <S.Field>
              <span>Name</span>
              <input value={formik.values.name} name="name" onChange={formik.handleChange} />
            </S.Field>

            <S.Field>
              <span>Rarity</span>
              <Select
                name="rarity"
                options={rarityOptions}
                value={formik.values.rarity}
                setFieldValue={formik.setFieldValue}
              />
            </S.Field>

            <S.Field>
              <span>Position</span>
              <Select
                name="position"
                options={positionOptions}
                value={formik.values.position}
                setFieldValue={formik.setFieldValue}
              />
            </S.Field>

            <S.Field>
              <span>Type</span>
              <Select
                name="type"
                options={typeOptions}
                value={formik.values.type}
                isMulti
                setFieldValue={formik.setFieldValue}
              />
            </S.Field>

            <S.Field>
              <span>Team</span>
              <Select
                name="bonusTeam"
                options={teamsOptions}
                value={formik.values.bonusTeam}
                isMulti
                setFieldValue={formik.setFieldValue}
              />
            </S.Field>

            <S.Field>
              <span>Url</span>
              <input value={formik.values.url} name="url" onChange={formik.handleChange} />
            </S.Field>

            <S.Field>
              <span>Skills</span>

              <S.SkillsLevels>
                {[...Array(5)].map((_, i) => (
                  <S.Skills key={i}>
                    <S.SkillStar>
                      {i + 1}
                      {StarIcon}
                    </S.SkillStar>

                    {formik.values.skills[i + 1].map(({ name, level }, j) => (
                      <S.SkillsInput style={{ display: "flex", justifyContent: "center" }} key={j}>
                        <Select
                          name={`skills[${i + 1}][${j}].name`}
                          options={skillOptions}
                          value={formik.values.skills[i + 1][j].name}
                          onChange={(e) => onSkillNameChange(e, j)}
                        />
                        <input
                          placeholder="Level"
                          type="number"
                          name={`skills[${i + 1}][${j}].level`}
                          value={formik.values.skills[i + 1][j].level}
                          onChange={(e) => onSkillLevelChange(e, i + 1, j)}
                        />
                      </S.SkillsInput>
                    ))}

                    <S.AddSkill onClick={addSkill.bind(null, i + 1)}>{PlusIcon} Add skill</S.AddSkill>
                  </S.Skills>
                ))}
              </S.SkillsLevels>
            </S.Field>

            <S.Field>
              <span>Dev Bonus</span>

              <S.SkillsLevels style={{ display: "block" }}>
                {[...Array(5)].map((_, i) => (
                  <S.Skills key={i}>
                    <S.SkillStar>
                      {i + 1}
                      {StarIcon}
                    </S.SkillStar>

                    <S.SkillsInput style={{ display: "flex", justifyContent: "center" }}>
                      <Select
                        name={`devBonus[${i + 1}]`}
                        options={devBonusesOptions}
                        value={formik.values.devBonus[i + 1]}
                        onChange={(value) => onDevBonusChange(value, i + 1)}
                        isMulti
                      />
                    </S.SkillsInput>
                  </S.Skills>
                ))}
              </S.SkillsLevels>
            </S.Field>

            <S.Field>
              <Button onClick={onExportJson}>Export JSON</Button>
              <a id="downloadAnchorElem" />
            </S.Field>
          </form>
        </div>
      </S.Container>
    </Layout>
  );
};

export default TrainerPage;
